import { Suspense, useContext, useEffect, useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { LoadingView } from "../components";
import { DailyCheckContextProvider, DeviceContextProvider, LoginContextProvider } from "../context";
import { AgentContextProvider } from "../context/AgentContext";
import { AppContextProvider } from "../context/AppContext";
import { FocusContextProvider } from "../context/FocusContext";
import { TawkContextProvider } from "../context/TawkContext";
import { RecentPlayGamesContextProvider } from "../pages/Home/context/RecentPlayGamesContext";
import { safeToggleChatwootBubble } from "../plugins/safeChatwootBubble";
import { envChangeType, envType } from "../plugins/utils";
import RouterContext from "./RouterContext";
import layouts from "./layouts";
import RouterList from "./router";

const findRouteConfig = (currentPath = "") => {
  let target = RouterList.find((route) => {
    return route.path === currentPath ?? window.location.pathname;
  });
  if (!target) return "default";

  return !!target ? target : {}; //background
};

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return <Component {...props} router={{ location, navigate, params }} />;
  }

  return ComponentWithRouterProp;
}

const RouterRender = ({ router, match, showTransform }) => {
  let { location } = router;
  const { changeLayout, changeNavType } = useContext(RouterContext);

  useEffect(() => {
    // console.log(location);
    changeLayout(findRouteConfig(location.pathname)?.layout);
    changeNavType(findRouteConfig(location.pathname)?.navType);
  }, [location]);

  // Chatwoot Bubble only show on home page
  useEffect(() => {
    const syncChatwootBubble = () => {
      safeToggleChatwootBubble(
        location.pathname === "/"
          ? envChangeType("REACT_APP_CHATWOOT_ICON_IS_CUSTOMIZED", envType.BOOLEAN)
            ? "hide"
            : "show"
          : "hide",
      );
    };
    syncChatwootBubble();
    window.addEventListener("chatwoot:ready", syncChatwootBubble);
    return () => window.removeEventListener("chatwoot:ready", syncChatwootBubble);
  }, [location.pathname]);

  return (
    <TransitionGroup>
      <CSSTransition classNames={"fade"} in={match != null} timeout={0} key={location?.key} appear>
        <Suspense fallback={<LoadingView />}>
          <Routes>
            {RouterList.map((route, index) => {
              return (
                <Route
                  key={index}
                  path={route.path}
                  element={<route.component {...route.props} />}
                />
              );
            })}
          </Routes>
        </Suspense>
      </CSSTransition>
    </TransitionGroup>
  );
};

const RouterRenderWithRouter = withRouter(RouterRender);

const ProcessRouter = () => {
  const [layout, setLayout] = useState(findRouteConfig()?.layout ?? "default");
  const [navType, setNavType] = useState("");
  const actions = {
    changeLayout: (layout = "default") => {
      setLayout(layout ?? "default");
    },
    changeNavType: (navType = {}) => {
      setNavType(navType ?? {});
    },
  };

  var LayoutComponet = layouts[layout];

  return (
    <RouterContext.Provider value={actions}>
      <Router>
        <FocusContextProvider>
          <DeviceContextProvider>
            <LoginContextProvider>
              <AppContextProvider>
                <DailyCheckContextProvider>
                  <AgentContextProvider>
                    <LayoutComponet navType={navType ? navType : { type: "default" }}>
                      <RecentPlayGamesContextProvider>
                        <TawkContextProvider>
                          <RouterRenderWithRouter />
                        </TawkContextProvider>
                      </RecentPlayGamesContextProvider>
                    </LayoutComponet>
                  </AgentContextProvider>
                </DailyCheckContextProvider>
              </AppContextProvider>
            </LoginContextProvider>
          </DeviceContextProvider>
        </FocusContextProvider>
      </Router>
    </RouterContext.Provider>
  );
};

export default ProcessRouter;
